import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, brl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Truck, QrCode, CreditCard, FileText, Store, MapPin, MessageCircle } from "lucide-react";
import CardFields from "./CardFields";
import { tokenizeCard } from "@/lib/pagarme";

const PAYMENTS = [
  { id: "pix", label: "PIX (5% off)", icon: QrCode },
  { id: "cartao", label: "Cartão de crédito", icon: CreditCard },
  { id: "boleto", label: "Boleto", icon: FileText },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ cep: "", street: "", number: "", complement: "", district: "", city: "", state: "SP" });
  /* "entrega" ou "retirada". É a PRIMEIRA decisão da tela porque muda tudo o
     que vem depois: quem vai buscar no balcão não deve preencher CEP nem ver
     transportadora, e antes daqui não havia como não pagar frete. */
  const [modo, setModo] = useState("entrega");
  const [loja, setLoja] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [payment, setPayment] = useState("pix");
  // Dados do cartão vivem SÓ aqui, no navegador. Nada disso vai para o backend:
  // o que sobe é o token que a Pagar.me devolve.
  const [cartao, setCartao] = useState(null);
  const [parcelas, setParcelas] = useState(1);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);   // { code, percent } quando validado
  const [couponMsg, setCouponMsg] = useState(null);     // { ok, text }
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Endereço da loja, para o cliente saber ONDE retirar antes de confirmar.
  useEffect(() => {
    api.get("/settings").then((r) => setLoja(r.data)).catch(() => {});
  }, []);

  /* Escolher retirada já define o frete: não há o que calcular nem o que
     escolher, e obrigar a clicar em "Calcular frete" para receber uma única
     opção de R$ 0 seria um passo que não decide nada. */
  useEffect(() => {
    if (modo === "retirada") {
      setShipping({ method: "retirada", carrier: "Retirar na loja", cost: 0, days: 0 });
      setQuotes([]);
    } else {
      setShipping(null);
    }
  }, [modo]);

  const set = (k) => (e) => setAddress({ ...address, [k]: e.target.value });
  const cepMask = (v) => v.replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

  // Preenche o endereço pelo CEP (ViaCEP). Falha em silêncio — dá para digitar à mão.
  const lookupCep = async (cepRaw) => {
    if (cepRaw.replace(/\D/g, "").length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepRaw.replace(/\D/g, "")}/json/`);
      const d = await res.json();
      if (d && !d.erro) setAddress((a) => ({ ...a, street: d.logradouro || a.street, district: d.bairro || a.district, city: d.localidade || a.city, state: d.uf || a.state }));
    } catch { /* mantém o que foi digitado */ }
  };

  const getQuotes = async () => {
    if (address.cep.replace(/\D/g, "").length !== 8) { toast.error("Informe um CEP válido (8 dígitos)."); return; }
    setLoadingQuotes(true);
    try {
      const { data } = await api.post("/shipping/quote", {
        cep: address.cep,
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
      });
      setQuotes(data);
      setShipping(data[0]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoadingQuotes(false); }
  };

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) { setCouponMsg({ ok: false, text: "Digite um cupom." }); return; }
    setApplyingCoupon(true); setCouponMsg(null);
    try {
      // Os itens vão junto: cupom de valor fixo, com mínimo de pedido ou
      // restrito a uma categoria não se calcula sem saber o que está no
      // carrinho. O servidor devolve o desconto em reais já resolvido.
      const { data } = await api.post("/coupons/validate", {
        code,
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
      });
      if (data.valid) {
        setCouponInfo({ code: data.code, percent: data.percent, discount: data.discount, type: data.discount_type });
        setCouponMsg({ ok: true, text: `Cupom ${data.code} aplicado — ${brl(data.discount)} de desconto.` });
      } else {
        setCouponInfo(null);
        // A mensagem vem pronta do servidor ("expirou em 12/08", "vale a partir
        // de R$ 200"): é ela que evita a ligação para o balcão.
        setCouponMsg({ ok: false, text: data.message || "Cupom inválido." });
      }
    } catch (e) { setCouponInfo(null); setCouponMsg({ ok: false, text: apiError(e) }); }
    finally { setApplyingCoupon(false); }
  };

  /* O desconto foi calculado para os itens que estavam no carrinho na hora de
     aplicar. Mudou o carrinho, a conta mudou junto — e um cupom com mínimo de
     pedido ou escopo pode até ter deixado de valer. Cai fora e pede de novo,
     em vez de exibir um valor que o checkout vai recusar. */
  const cartKey = items.map((i) => `${i.variant_id}:${i.qty}`).join("|");
  useEffect(() => {
    setCouponInfo(null);
    setCouponMsg(null);
  }, [cartKey]);

  // Mesma ordem do backend: cupom primeiro, depois PIX (5%) sobre o restante.
  // O valor do cupom vem CALCULADO do servidor — recalcular aqui pelo percentual
  // mostraria um desconto que o checkout depois recusaria, justamente nos casos
  // em que o cliente menos entenderia (valor fixo, escopo por categoria).
  const couponDiscount = couponInfo?.discount || 0;
  const pixDiscount = payment === "pix" ? Math.round((subtotal - couponDiscount) * 5) / 100 : 0;
  const total = subtotal - couponDiscount - pixDiscount + (shipping?.cost || 0);

  /* O cartão some quando a loja não pode cobrar por ele: sem a Pagar.me
     configurada, a cobrança falharia no fim — depois de o cliente confirmar,
     com o estoque já baixado e o pedido criado. É melhor não oferecer. */
  const cartaoDisponivel = !!loja?.card_enabled;
  const formasDisponiveis = PAYMENTS.filter((p) => p.id !== "cartao" || cartaoDisponivel);

  /* Escolha que deixou de existir não pode ficar selecionada: se a loja
     desligar o cartão enquanto alguém está no checkout, o botão sumiria com a
     seleção presa nele e o pedido sairia sem forma de pagamento válida. */
  useEffect(() => {
    if (payment === "cartao" && !cartaoDisponivel) setPayment("pix");
  }, [payment, cartaoDisponivel]);

  const submit = async () => {
    if (!shipping) { toast.error("Calcule o frete pelo CEP"); return; }
    if (modo === "entrega" && (!address.street || !address.number || !address.city)) {
      toast.error("Preencha o endereço completo"); return;
    }
    setSubmitting(true);
    try {
      /* O cartão vira TOKEN antes de qualquer coisa: se a tokenização falhar,
         nada foi criado ainda — sem pedido órfão e sem estoque baixado. Fazer
         isso depois de criar o pedido deixaria a venda registrada e sem
         cobrança, que é o pior dos dois lados. */
      let cardToken = null;
      if (payment === "cartao") {
        if (!cartao?.number || !cartao?.holder_name || !cartao?.exp || !cartao?.cvv) {
          toast.error("Preencha os dados do cartão."); setSubmitting(false); return;
        }
        try {
          cardToken = await tokenizeCard(loja.pagarme_public_key, cartao);
        } catch (e) {
          toast.error(e.message); setSubmitting(false); return;
        }
      }

      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        // Retirada não manda endereço: não há entrega, e um endereço inventado
        // ficaria gravado no pedido como destino que ninguém vai usar.
        address: modo === "retirada" ? undefined : address,
        shipping_method: shipping.method, shipping_cost: shipping.cost,
        payment_method: payment, coupon_code: couponInfo?.code || null,
        card_token: cardToken,
        installments: payment === "cartao" ? parcelas : undefined,
      });
      clear();
      navigate("/pedido-confirmado", { state: { order: data } });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (items.length === 0) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-500" data-testid="checkout-empty">Seu carrinho está vazio.</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 space-y-8">
        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Truck size={18} className="text-brand-600" /> Como você quer receber
          </h2>

          {/* A ESCOLHA VEM PRIMEIRO. Com o formulário de endereço no topo, quem
              ia buscar no balcão preenchia CEP, rua e número à toa — e só
              descobria a retirada depois, se descobrisse. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {[
              { id: "entrega", icon: Truck, titulo: "Receber em casa", desc: "Correios ou transportadora" },
              { id: "retirada", icon: Store, titulo: "Retirar na loja", desc: "Sem frete" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setModo(o.id)}
                aria-pressed={modo === o.id}
                data-testid={`checkout-modo-${o.id}`}
                className={`text-left border rounded-xl p-4 transition-colors ${
                  modo === o.id ? "border-brand-600 bg-brand-50 ring-1 ring-brand-200" : "border-slate-200 hover:border-slate-300"}`}
              >
                <span className="flex items-center gap-2 font-medium text-slate-900">
                  <o.icon size={17} className={modo === o.id ? "text-brand-600" : "text-slate-400"} aria-hidden />
                  {o.titulo}
                </span>
                <span className={`block text-xs mt-1 ${o.id === "retirada" ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>
                  {o.desc}
                </span>
              </button>
            ))}
          </div>

          {modo === "retirada" ? (
            /* ONDE retirar, na hora da escolha. Sem isto o cliente confirma o
               pedido sem saber para onde ir, e o endereço só chegaria depois
               por WhatsApp — que é exatamente a ligação que a loja não quer. */
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="checkout-loja-endereco">
              <p className="text-eyebrow uppercase text-slate-500 mb-1.5">Retire em</p>
              <p className="font-medium text-slate-900">{loja?.store_name || "Ortopédica São Paulo"}</p>
              {loja?.address
                ? <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{loja.address}</p>
                : <p className="text-sm text-slate-500 mt-0.5">Combinaremos o local pelo WhatsApp após a confirmação.</p>}

              <div className="flex flex-wrap gap-3 mt-3">
                {loja?.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loja.address)}`}
                    target="_blank" rel="noreferrer" data-testid="checkout-loja-mapa"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
                  >
                    <MapPin size={14} aria-hidden /> Ver no mapa
                  </a>
                )}
                {loja?.whatsapp && (
                  <a href={`https://wa.me/${String(loja.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-brand-600">
                    <MessageCircle size={14} aria-hidden /> Falar com a loja
                  </a>
                )}
              </div>

              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Avisaremos quando o pedido estiver separado. Leve um documento com foto para retirar.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 flex gap-2">
                  <input value={address.cep} onChange={(e) => setAddress({ ...address, cep: cepMask(e.target.value) })} onBlur={(e) => lookupCep(e.target.value)}
                    inputMode="numeric" aria-label="CEP" placeholder="CEP (00000-000)" data-testid="checkout-cep-input"
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                  <button onClick={getQuotes} disabled={loadingQuotes} data-testid="checkout-quote-btn" className="bg-slate-900 text-white text-sm font-medium px-4 rounded-lg hover:bg-slate-700 disabled:opacity-50">{loadingQuotes ? "Calculando..." : "Calcular frete"}</button>
                </div>
                <input value={address.street} onChange={set("street")} aria-label="Rua ou avenida" placeholder="Rua / Avenida" data-testid="checkout-street-input" className="col-span-2 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <input value={address.number} onChange={set("number")} aria-label="Número" placeholder="Número" data-testid="checkout-number-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <input value={address.complement} onChange={set("complement")} aria-label="Complemento" placeholder="Complemento" data-testid="checkout-complement-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <input value={address.district} onChange={set("district")} aria-label="Bairro" placeholder="Bairro" data-testid="checkout-district-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <input value={address.city} onChange={set("city")} aria-label="Cidade" placeholder="Cidade" data-testid="checkout-city-input" className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                  <input value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase().slice(0, 2) })} aria-label="UF" placeholder="UF" maxLength={2} data-testid="checkout-state-input" className="w-14 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              {quotes.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="shipping-options">
                  {/* A retirada já é a outra opção lá em cima: repeti-la aqui
                      seria a mesma escolha em dois lugares, discordando. */}
                  {quotes.filter((q) => q.method !== "retirada").map((q) => (
                    <label key={q.method} className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer ${shipping?.method === q.method ? "border-brand-600 bg-brand-50" : "border-slate-200"}`}>
                      <span className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={shipping?.method === q.method} aria-label={`Frete ${q.method}`} onChange={() => setShipping(q)} data-testid={`shipping-option-${q.method}`} />
                        <span className="font-medium">{q.method}</span><span className="text-slate-500">· {q.carrier} · {q.days} dias úteis</span>
                      </span>
                      <span className={`font-mono text-sm font-semibold ${q.cost === 0 ? "text-emerald-600" : ""}`}>{q.cost === 0 ? "Grátis" : brl(q.cost)}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">Pagamento</h2>
          <div className={`grid gap-3 ${formasDisponiveis.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
            {formasDisponiveis.map((p) => (
              <button key={p.id} onClick={() => setPayment(p.id)} data-testid={`payment-option-${p.id}`}
                aria-pressed={payment === p.id}
                className={`border rounded-lg p-4 text-sm font-medium flex flex-col items-center gap-2 transition-colors ${payment === p.id ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                <p.icon size={20} />{p.label}
              </button>
            ))}
          </div>

          {payment === "cartao" && cartaoDisponivel && (
            <CardFields valor={total} parcelas={parcelas} onParcelasChange={setParcelas} onChange={setCartao} />
          )}
        </section>
      </div>

      <aside className="lg:col-span-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 sticky top-24">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">Resumo do pedido</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto scrollbar-thin">
            {items.map((i) => (
              <div key={`${i.product_id}:${i.variant_id}`} className="flex justify-between text-sm">
                <span className="text-slate-600">{i.qty}x {i.name} <span className="text-slate-400">({i.variant_label})</span></span>
                <span className="font-mono shrink-0 ml-2">{brl(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="flex gap-2">
              <input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); setCouponMsg(null); }} placeholder="Cupom (ex: BEMVINDO10)" data-testid="checkout-coupon-input"
                aria-label="Cupom de desconto" className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm uppercase" />
              <button onClick={applyCoupon} disabled={applyingCoupon} data-testid="checkout-coupon-apply"
                className="bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 text-sm font-semibold px-4 rounded-lg">{applyingCoupon ? "..." : "Aplicar"}</button>
            </div>
            {couponMsg && <p className={`text-xs mt-1.5 ${couponMsg.ok ? "text-emerald-600" : "text-red-500"}`} data-testid="checkout-coupon-msg">{couponMsg.text}</p>}
          </div>
          <div className="border-t border-slate-200 mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{brl(subtotal)}</span></div>
            {couponDiscount > 0 && <div className="flex justify-between text-emerald-600"><span>Cupom {couponInfo.code}{couponInfo.type !== "amount" && couponInfo.percent ? ` (${couponInfo.percent}%)` : ""}</span><span className="font-mono" data-testid="checkout-coupon-discount">-{brl(couponDiscount)}</span></div>}
            {pixDiscount > 0 && <div className="flex justify-between text-emerald-600"><span>Desconto PIX (5%)</span><span className="font-mono" data-testid="checkout-pix-discount">-{brl(pixDiscount)}</span></div>}
            {/* Na retirada o rótulo diz o que É, e não "frete grátis": não há
                frete nenhum, e o cliente precisa reconhecer no resumo a escolha
                que fez lá em cima antes de confirmar. */}
            <div className="flex justify-between text-slate-600">
              <span>{modo === "retirada" ? "Retirada na loja" : "Frete"}</span>
              <span className={`font-mono ${shipping?.cost === 0 ? "text-emerald-600" : ""}`} data-testid="checkout-shipping-cost">
                {shipping ? (shipping.cost === 0 ? "Grátis" : brl(shipping.cost)) : "—"}
              </span>
            </div>
            <div className="flex justify-between text-slate-900 font-semibold text-base pt-2"><span>Total</span><span className="font-mono" data-testid="checkout-total">{brl(total)}</span></div>
          </div>
          <button onClick={submit} disabled={submitting} data-testid="checkout-submit-btn"
            className="w-full mt-5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors">
            {submitting ? "Processando..." : "Confirmar pedido"}
          </button>
        </div>
      </aside>
    </div>
  );
}
